/**
 * Match telemetry: the measurements the achievement system asks for.
 *
 * Most of these cannot be read off the final state. "The lowest your Integrity
 * ever got" and "the most Legendary characters you controlled at once" are
 * facts about the whole game, not about its last frame, so they are accumulated
 * as the match is played: `MatchController` calls `observe` after every reduce
 * with the events that reduce produced and the state it produced them from.
 *
 * Everything here is derived from engine events and engine queries. Nothing
 * branches on a card name or id, and nothing decides a rule: a death is a death
 * because the engine emitted `Died`, not because this file worked out that a
 * creature should be dead.
 *
 * Fields are optional in `MatchSummary` on purpose. The evaluator treats an
 * absent field as "not measured" rather than as false, so a measurement this
 * file cannot make honestly is left off instead of guessed at.
 */

import {
  getCardDef,
  getEffectiveStats,
  hasKeyword,
  profile,
  totalCost,
  type Faction,
  type GameEvent,
  type GameState,
} from '@/engine';
import type { MatchResult, MatchSummary } from '@/app/stores/matchHistory';

/** A card costing this much or more counts as "expensive" for the badge. */
const EXPENSIVE_COST = 6;

/** Blocking this many attackers in one combat and surviving is the badge. */
const MANY_BLOCKERS = 4;

export interface MatchTelemetry {
  localPlayer: string;
  opponent: string;
  startingIntegrity: number;

  minIntegrity: number;
  lostIntegrity: boolean;
  redrew: boolean;

  /** Integrity taken off the opponent in the combat phase now in progress. */
  damageThisCombat: number;
  maxDamageOneCombat: number;

  /** Deaths credited to the stack item that is currently resolving. */
  deathsThisResolution: number;
  maxDestroyedByOneCard: number;

  legendariesControlledAtOnce: number;
  maxAttackControlled: number;
  earliestExpensiveDeploy: number | null;

  frontrunKill: boolean;
  blocked4Survived: boolean;
  /** Set while a combat the local player blocked heavily is still running. */
  pendingBigBlock: boolean;

  /** Opponent Integrity immediately before the blow that ended the game. */
  exactLethal: boolean;

  /** Combat step the engine last announced, for attributing deaths. */
  combatStep: string | null;
}

export function createTelemetry(state: GameState, localPlayer: string): MatchTelemetry {
  const opponent = state.playerOrder.find((id) => id !== localPlayer) ?? localPlayer;
  return {
    localPlayer,
    opponent,
    startingIntegrity: state.options.startingIntegrity,
    minIntegrity: state.players[localPlayer]?.integrity ?? state.options.startingIntegrity,
    lostIntegrity: false,
    redrew: false,
    damageThisCombat: 0,
    maxDamageOneCombat: 0,
    deathsThisResolution: 0,
    maxDestroyedByOneCard: 0,
    legendariesControlledAtOnce: 0,
    maxAttackControlled: 0,
    earliestExpensiveDeploy: null,
    frontrunKill: false,
    blocked4Survived: false,
    pendingBigBlock: false,
    exactLethal: false,
    combatStep: null,
  };
}

/**
 * Fold one reduce's worth of events into the accumulator.
 *
 * `before` is the state the action was applied to and `after` is the result.
 * Both are needed: the opponent's Integrity before the final blow is what makes
 * a kill exact, and the board after the action is what makes a count current.
 */
export function observe(
  telemetry: MatchTelemetry,
  before: GameState,
  after: GameState,
  events: GameEvent[],
): void {
  const { localPlayer, opponent } = telemetry;
  const opponentBefore = before.players[opponent]?.integrity ?? 0;

  for (const event of events) {
    switch (event.type) {
      case 'HandRedrawn':
        if (event.player === localPlayer) telemetry.redrew = true;
        break;

      case 'PhaseChanged':
        // Entering combat starts a new window for the "one combat" measures.
        if (event.phase === 'combat') telemetry.damageThisCombat = 0;
        if (event.phase !== 'combat') telemetry.combatStep = null;
        break;

      case 'CombatStepChanged':
        telemetry.combatStep = event.step;
        break;

      case 'IntegrityChanged':
        if (event.player === localPlayer) {
          telemetry.minIntegrity = Math.min(telemetry.minIntegrity, event.total);
          if (event.delta < 0) telemetry.lostIntegrity = true;
        }
        break;

      case 'DamageDealtToPlayer':
        if (event.targetPlayer === opponent) {
          telemetry.damageThisCombat += event.amount;
          telemetry.maxDamageOneCombat = Math.max(
            telemetry.maxDamageOneCombat,
            telemetry.damageThisCombat,
          );
        }
        break;

      case 'BlockersDeclared':
        if (event.player === localPlayer && event.blocks.length >= MANY_BLOCKERS) {
          telemetry.pendingBigBlock = true;
        }
        break;

      case 'StackItemResolved':
        // A new resolution: deaths from here on belong to the next card.
        telemetry.deathsThisResolution = 0;
        break;

      case 'Died':
        telemetry.deathsThisResolution += 1;
        telemetry.maxDestroyedByOneCard = Math.max(
          telemetry.maxDestroyedByOneCard,
          telemetry.deathsThisResolution,
        );
        // A kill in the Frontrun step, on the turn the local player attacked,
        // is a Frontrun attacker killing its blocker.
        if (
          telemetry.combatStep === 'frontrunDamage' &&
          before.activePlayer === localPlayer &&
          event.player === opponent
        ) {
          telemetry.frontrunKill = true;
        }
        break;

      case 'Deployed':
        if (event.player === localPlayer) {
          const cost = costOf(after, event.instanceId);
          if (cost >= EXPENSIVE_COST) {
            telemetry.earliestExpensiveDeploy =
              telemetry.earliestExpensiveDeploy === null
                ? event.turn
                : Math.min(telemetry.earliestExpensiveDeploy, event.turn);
          }
        }
        break;

      case 'GameEnded':
        // The blow was exact if it left the loser on precisely zero rather than
        // overshooting into negative Integrity.
        if (event.winner === localPlayer && opponentBefore > 0) {
          telemetry.exactLethal = (after.players[opponent]?.integrity ?? 0) === 0;
        }
        break;

      default:
        break;
    }
  }

  // Combat is over: a big block that the local player lived through counts.
  if (telemetry.pendingBigBlock && after.phase !== 'combat') {
    telemetry.pendingBigBlock = false;
    if (!after.players[localPlayer]?.lost) telemetry.blocked4Survived = true;
  }

  sampleBoard(telemetry, after);
}

/**
 * Point-in-time measurements, taken after every action.
 *
 * Integrity is sampled here as well as watched through `IntegrityChanged`,
 * because not every route to a changed total emits that event: a Test Lab dev
 * action sets it directly. Sampling the state is the measurement that cannot be
 * bypassed, and the event handler catches a dip that is healed again inside a
 * single reduce, which sampling would miss.
 */
function sampleBoard(telemetry: MatchTelemetry, state: GameState): void {
  const player = state.players[telemetry.localPlayer];
  if (!player) return;

  telemetry.minIntegrity = Math.min(telemetry.minIntegrity, player.integrity);
  if (player.integrity < telemetry.startingIntegrity) telemetry.lostIntegrity = true;

  let legendaries = 0;
  for (const id of player.battlefield.characters) {
    const object = state.objects[id];
    if (!object) continue;
    if (profile(state, id).legendary) legendaries += 1;
    telemetry.maxAttackControlled = Math.max(
      telemetry.maxAttackControlled,
      getEffectiveStats(state, id).attack,
    );
  }
  telemetry.legendariesControlledAtOnce = Math.max(
    telemetry.legendariesControlledAtOnce,
    legendaries,
  );
}

function costOf(state: GameState, instanceId: string): number {
  const object = state.objects[instanceId];
  if (!object?.cardId) return 0;
  const def = getCardDef(object.cardId);
  return def ? totalCost(def.cost) : 0;
}

export interface SummaryInput {
  telemetry: MatchTelemetry;
  state: GameState;
  mode: MatchSummary['mode'];
  playerDeckId: string;
  opponentDeckId: string;
  opponentName: string;
  difficulty: MatchSummary['difficulty'];
  seed: number;
  /** Card ids in the local player's deck, for the faction measurement. */
  playerDeck: string[];
}

/**
 * Turn the accumulator plus the final state into the summary the history store
 * and the achievement evaluator read.
 */
export function buildSummary(input: SummaryInput): Omit<MatchSummary, 'id' | 'playedAt'> {
  const { telemetry, state } = input;
  const { localPlayer, opponent, startingIntegrity } = telemetry;

  const result: MatchResult = state.draw ? 'draw' : state.winner === localPlayer ? 'win' : 'loss';
  const playerIntegrityEnd = state.players[localPlayer]?.integrity ?? 0;
  const opponentIntegrityEnd = state.players[opponent]?.integrity ?? 0;

  const factionsUsed = factionsOf(input.playerDeck);

  return {
    result,
    mode: input.mode,
    playerDeckId: input.playerDeckId,
    opponentDeckId: input.opponentDeckId,
    opponentName: input.opponentName,
    difficulty: input.difficulty,
    turns: state.turn,
    damageDealt: Math.max(0, startingIntegrity - opponentIntegrityEnd),
    damageTaken: Math.max(0, startingIntegrity - playerIntegrityEnd),
    seed: input.seed,

    playerIntegrityEnd,
    minIntegrity: telemetry.minIntegrity,
    lostIntegrity: telemetry.lostIntegrity,
    exactLethal: telemetry.exactLethal,
    maxDamageOneCombat: telemetry.maxDamageOneCombat,
    maxDestroyedByOneCard: telemetry.maxDestroyedByOneCard,
    factionsUsed,
    deckFactionCount: factionsUsed.length,
    legendariesControlledAtOnce: telemetry.legendariesControlledAtOnce,
    earliestExpensiveDeploy: telemetry.earliestExpensiveDeploy,
    deckEmptyAtWin: result === 'win' && (state.players[localPlayer]?.deck.length ?? 0) === 0,
    redrew: telemetry.redrew,
    drainerLinkWin: result === 'win' && controlsDrainerLink(state, localPlayer),
    frontrunKill: telemetry.frontrunKill,
    blocked4Survived: telemetry.blocked4Survived,
    maxAttackControlled: telemetry.maxAttackControlled,
  };
}

/** Distinct non-neutral factions in a flat deck list. */
export function factionsOf(cardIds: string[]): Faction[] {
  const seen = new Set<Faction>();
  for (const id of cardIds) {
    const def = getCardDef(id);
    if (!def || def.faction === 'neutral') continue;
    seen.add(def.faction);
  }
  return [...seen].sort();
}

function controlsDrainerLink(state: GameState, playerId: string): boolean {
  const player = state.players[playerId];
  if (!player) return false;
  return player.battlefield.characters.some((id) => hasKeyword(state, id, 'drainerLink'));
}
